import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import api from "../Utils/Axios";
import ReceiptView from "./ReceiptView";

function ReceiptManagement() {
  const navigate = useNavigate();
  const [ledgerParties, setLedgerParties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [companyLanguage, setCompanyLanguage] = useState('en');
  // New: separate tabs for suppliers and customers
  const [activeTab, setActiveTab] = useState('suppliers'); // 'suppliers' | 'customers'
  const [selectedParty, setSelectedParty] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [company, setCompany] = useState(null);
  const [selectedParties, setSelectedParties] = useState([]); // Array of selected party IDs
  const [multipleReceiptsData, setMultipleReceiptsData] = useState([]); // Array of receipt data for multiple receipts
  const [generatingReceiptId, setGeneratingReceiptId] = useState(null); // Track which receipt is being generated

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    fetchParties();
    fetchCompany();
  }, []);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchCompany = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }
      const response = await api.get("/company/profile", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompany(response.data.company);
    } catch (error) {
      console.error("Fetch company error:", error);
    }
  };

  const fetchParties = async () => {
    try {
      setIsLoading(true);
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      // Fetch all parties with balances
      const response = await api.get('/ledger/parties', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allParties = response.data.parties || [];
      // Filter parties with non-zero balances (receivable or payable)
      const partiesWithBalance = allParties.filter(party => {
        const balance = party.currentBalance || 0;
        return balance !== 0;
      });

      setLedgerParties(partiesWithBalance);
    } catch (error) {
      console.error('Fetch parties error:', error);
      toast.error(companyLanguage === 'ur' ? 'پارٹیز نہیں مل سکیں' : 'Failed to load parties');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateReceipt = async (party) => {
    setGeneratingReceiptId(party._id);
    try {
      const token = getToken();
      if (!token) {
        setGeneratingReceiptId(null);
        return;
      }

      // Fetch party transactions to get details
      const transactionsResponse = await api.get(`/ledger/parties/${party._id}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const transactions = transactionsResponse.data.transactions || [];
      const balance = party.currentBalance || 0;
      const isReceivable = balance > 0;

      // Get latest transaction with billId (same as customer/supplier khata)
      // They use tx.billId.toString().slice(-6) for voucher number and tx.date for date
      let latestTransaction = null;
      let previousBalance = 0;
      let newBillAmount = 0;
      let billNumberText = null;

      // Find the latest transaction with billId (debit type = sale/bill)
      const transactionsWithBillId = transactions
        .filter(tx => tx.billId && tx.type === 'debit')
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      if (transactionsWithBillId.length > 0) {
        latestTransaction = transactionsWithBillId[0];

        // Get bill number from transaction description (same as customer/supplier khata)
        // They show: "بل # 1" format from description
        // Description format: "Bill #1 (سیل)" or "بل #1 (سیل)"
        let billNumber = null;
        let billDate = latestTransaction.date;

        // Extract bill number from description (same logic as customer/supplier khata)
        if (latestTransaction.description) {
          // Remove suffixes like " (سیل)", " (ریکوری)", " (Sale)", " (Recovery)"
          const cleanDescription = latestTransaction.description
            .replace(' (سیل)', '')
            .replace(' (ریکوری)', '')
            .replace(' (Sale)', '')
            .replace(' (Recovery)', '')
            .trim();

          // Check if description contains "بل #" or "Bill #"
          const billMatch = cleanDescription.match(/(?:بل|Bill)\s*#\s*(\d+)/i);
          if (billMatch) {
            billNumber = billMatch[1];
            billNumberText = companyLanguage === 'ur' ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
          } else {
            // Fallback: use billId.slice(-6) like voucher number
            billNumber = latestTransaction.billId.toString().slice(-6);
            billNumberText = companyLanguage === 'ur' ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
          }
        } else {
          // Fallback: use billId.slice(-6) like voucher number
          billNumber = latestTransaction.billId.toString().slice(-6);
          billNumberText = companyLanguage === 'ur' ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
        }

        // Calculate new bill amount from transaction
        newBillAmount = latestTransaction.amount || 0;

        // Calculate previous balance
        previousBalance = Math.abs(balance) - newBillAmount;
        if (previousBalance < 0) previousBalance = 0;

        console.log('Latest transaction with bill:', {
          billId: latestTransaction.billId,
          billNumber: billNumber,
          billNumberText: billNumberText,
          billDate: billDate,
          description: latestTransaction.description,
          amount: newBillAmount,
          transaction: latestTransaction
        });
      } else {
        // No transactions with billId found
        previousBalance = Math.abs(balance);
        console.log('No transactions with billId found');
      }

      // Prepare receipt data (same format as customer/supplier khata)
      const receipt = {
        party: party,
        balance: Math.abs(balance),
        isReceivable: isReceivable,
        transactions: transactions,
        date: new Date().toLocaleDateString('en-CA'),
        company: company,
        latestTransaction: latestTransaction,
        billNumber: billNumberText, // "بل # 1" format
        billDate: latestTransaction ? latestTransaction.date : null,
        previousBalance: previousBalance,
        newBillAmount: newBillAmount,
        totalPayable: Math.abs(balance)
      };

      console.log('Receipt Data Prepared:', {
        hasLatestTransaction: !!receipt.latestTransaction,
        billNumber: receipt.billNumber,
        billDate: receipt.billDate,
        receiptData: receipt
      });

      setReceiptData(receipt);
      setShowReceipt(true);
    } catch (error) {
      console.error('Generate receipt error:', error);
      toast.error(companyLanguage === 'ur' ? 'رسید بنانے میں ناکام' : 'Failed to generate receipt');
    } finally {
      setGeneratingReceiptId(null);
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setReceiptData(null);
    setMultipleReceiptsData([]);
    setSelectedParties([]);
  };

  const filteredParties = ledgerParties
    // Filter by active tab: suppliers or customers
    .filter(party => {
      if (activeTab === 'suppliers') return party.type === 'supplier';
      if (activeTab === 'customers') return party.type === 'customer';
      return true;
    })
    // Then apply search filter
    .filter(party => {
      if (!searchFilter) return true;
      const searchLower = searchFilter.toLowerCase();
      const name = (party.name || '').toLowerCase();
      const nameUrdu = (party.nameUrdu || '').toLowerCase();
      const phone = (party.phone || '').toLowerCase();
      return name.includes(searchLower) || nameUrdu.includes(searchLower) || phone.includes(searchLower);
    });

  const handleToggleSelection = (partyId) => {
    setSelectedParties(prev => {
      if (prev.includes(partyId)) {
        // Deselect
        return prev.filter(id => id !== partyId);
      } else {
        // Select (unlimited, but will be paginated 4 per page)
        return [...prev, partyId];
      }
    });
  };

  const handleSelectAll = () => {
    const allFilteredIds = filteredParties.map(party => party._id);
    const allSelected = allFilteredIds.every(id => selectedParties.includes(id));

    if (allSelected) {
      // Deselect all filtered parties
      setSelectedParties(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered parties
      setSelectedParties(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const isAllSelected = filteredParties.length > 0 && filteredParties.every(party => selectedParties.includes(party._id));
  const isSomeSelected = filteredParties.some(party => selectedParties.includes(party._id));
  const selectAllCheckboxRef = useRef(null);

  useEffect(() => {
    if (selectAllCheckboxRef.current) {
      selectAllCheckboxRef.current.indeterminate = isSomeSelected && !isAllSelected;
    }
  }, [isSomeSelected, isAllSelected]);

  const handlePrintSelected = async () => {
    if (selectedParties.length === 0) {
      toast.warning(companyLanguage === 'ur' ? 'براہ کرم کم از کم ایک رسید منتخب کریں' : 'Please select at least one receipt');
      return;
    }

    try {
      const token = getToken();
      if (!token) return;

      const receipts = [];

      for (const partyId of selectedParties) {
        const party = ledgerParties.find(p => p._id === partyId);
        if (!party) continue;

        // Fetch party transactions
        const transactionsResponse = await api.get(`/ledger/parties/${party._id}/transactions`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const transactions = transactionsResponse.data.transactions || [];
        const balance = party.currentBalance || 0;
        const isReceivable = balance > 0;

        // Get latest transaction with billId
        let latestTransaction = null;
        let previousBalance = 0;
        let newBillAmount = 0;
        let billNumberText = null;

        const transactionsWithBillId = transactions
          .filter(tx => tx.billId && tx.type === 'debit')
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        if (transactionsWithBillId.length > 0) {
          latestTransaction = transactionsWithBillId[0];

          if (latestTransaction.description) {
            const cleanDescription = latestTransaction.description
              .replace(' (سیل)', '')
              .replace(' (ریکوری)', '')
              .replace(' (Sale)', '')
              .replace(' (Recovery)', '')
              .trim();

            const billMatch = cleanDescription.match(/(?:بل|Bill)\s*#\s*(\d+)/i);
            if (billMatch) {
              billNumberText = companyLanguage === 'ur' ? `بل # ${billMatch[1]}` : `Bill # ${billMatch[1]}`;
            } else {
              const billNumber = latestTransaction.billId.toString().slice(-6);
              billNumberText = companyLanguage === 'ur' ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
            }
          } else {
            const billNumber = latestTransaction.billId.toString().slice(-6);
            billNumberText = companyLanguage === 'ur' ? `بل # ${billNumber}` : `Bill # ${billNumber}`;
          }

          newBillAmount = latestTransaction.amount || 0;
          previousBalance = Math.abs(balance) - newBillAmount;
          if (previousBalance < 0) previousBalance = 0;
        } else {
          previousBalance = Math.abs(balance);
        }

        const receipt = {
          party: party,
          balance: Math.abs(balance),
          isReceivable: isReceivable,
          transactions: transactions,
          date: new Date().toLocaleDateString('en-CA'),
          company: company,
          latestTransaction: latestTransaction,
          billNumber: billNumberText,
          billDate: latestTransaction ? latestTransaction.date : null,
          previousBalance: previousBalance,
          newBillAmount: newBillAmount,
          totalPayable: Math.abs(balance)
        };

        receipts.push(receipt);
      }

      setMultipleReceiptsData(receipts);
      setShowReceipt(true);
    } catch (error) {
      console.error('Print selected receipts error:', error);
      toast.error(companyLanguage === 'ur' ? 'رسیدیں بنانے میں ناکام' : 'Failed to generate receipts');
    }
  };

  const isUrdu = companyLanguage === 'ur';

  if (showReceipt && (receiptData || multipleReceiptsData.length > 0)) {
    return (
      <ReceiptView
        receiptData={receiptData}
        multipleReceiptsData={multipleReceiptsData}
        onClose={handleCloseReceipt}
        onPrint={handlePrintReceipt}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'رسید مینجمنٹ' : 'Receipt Management'} - Bexon</title>
      </Helmet>

      <div className={`min-h-screen bg-brand-background p-6 ${isUrdu ? 'font-urdu' : ''}`} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-brand-card">
            <h1 className={`text-3xl font-bold text-brand-header mb-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {isUrdu ? 'رسید مینجمنٹ' : 'Receipt Management'}
            </h1>
            <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {isUrdu ? 'گاہکوں اور سپلائرز کے لیے رسید بنائیں اور پرنٹ کریں' : 'Generate and print receipts for customers and suppliers'}
            </p>
          </div>

          {/* Tabs + Search Filter */}
          <div className="bg-white rounded-lg shadow-lg p-4 mb-6 border border-brand-card">
            {/* Tabs */}
            <div className={`flex mb-4 ${isUrdu ? 'flex-row-reverse' : ''}`}>
              <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setActiveTab('suppliers')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'suppliers' ? 'bg-brand-header text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'سپلائر رسیدیں' : 'Supplier Receipts'}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('customers')}
                  className={`px-4 py-2 text-sm font-semibold transition-all ${activeTab === 'customers' ? 'bg-brand-header text-white' : 'text-gray-700 hover:bg-gray-100'} ${isUrdu ? 'font-urdu' : ''}`}
                >
                  {isUrdu ? 'کسٹمر رسیدیں' : 'Customer Receipts'}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder={isUrdu ? 'نام، فون نمبر یا پتہ سے تلاش کریں...' : 'Search by name, phone, or address...'}
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                  style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", direction: 'rtl' } : {}}
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="text-gray-600 text-sm sm:text-base">
                  {isUrdu ? 'کل پارٹیز:' : 'Total Parties:'}{' '}
                  <span className="font-bold text-brand-header">{filteredParties.length}</span>
                  <span className="ml-2 text-xs text-gray-500">
                    {isUrdu
                      ? activeTab === 'suppliers'
                        ? '(سپلائر)'
                        : '(کسٹمر)'
                      : activeTab === 'suppliers'
                        ? '(Suppliers)'
                        : '(Customers)'}
                  </span>
                </div>
                {selectedParties.length > 0 && (
                  <div className="text-gray-600">
                    {isUrdu ? 'منتخب:' : 'Selected:'} <span className="font-bold text-brand-header">{selectedParties.length}</span>
                    {selectedParties.length > 0 && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({isUrdu ? `${Math.ceil(selectedParties.length / 4)} صفحات` : `${Math.ceil(selectedParties.length / 4)} pages`})
                      </span>
                    )}
                  </div>
                )}
                {selectedParties.length > 0 && (
                  <button
                    onClick={handlePrintSelected}
                    className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-6 py-2 rounded-lg transition-all transform hover:scale-105 hover:shadow-lg flex items-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {isUrdu ? `${selectedParties.length} رسیدیں پرنٹ کریں` : `Print ${selectedParties.length} Receipts`}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Parties List */}
          {isLoading ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-brand-card">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-header mx-auto"></div>
              <p className={`mt-4 text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
              </p>
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-brand-card">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className={`text-gray-600 text-lg ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {isUrdu ? 'کوئی پارٹی نہیں ملی' : 'No parties found'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-lg border border-brand-card overflow-hidden">
              {/* Table Header */}
              <div className={`bg-gray-50 border-b border-gray-200 px-6 py-4 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-1">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        ref={selectAllCheckboxRef}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header focus:ring-2 cursor-pointer"
                      />
                      <span className={`ml-2 text-sm font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                        {isUrdu ? 'سب منتخب' : 'Select All'}
                      </span>
                    </label>
                  </div>
                  <div className={`col-span-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'نام' : 'Name'}</span>
                  </div>
                  <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'فون' : 'Phone'}</span>
                  </div>
                  <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'پتہ' : 'Address'}</span>
                  </div>
                  <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'بقیہ رقم' : 'Balance'}</span>
                  </div>
                  <div className={`col-span-1 ${isUrdu ? 'text-right' : 'text-left'}`}>
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'قسم' : 'Type'}</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-sm font-semibold text-gray-700">{isUrdu ? 'عمل' : 'Action'}</span>
                  </div>
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-gray-200">
                {filteredParties.map((party) => {
                  const balance = party.currentBalance || 0;
                  const isReceivable = balance > 0;
                  const balanceAmount = Math.abs(balance);
                  const isSelected = selectedParties.includes(party._id);

                  return (
                    <div
                      key={party._id}
                      className={`px-6 py-4 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-emerald-50 border-l-4 border-brand-header' : ''
                        }`}
                    >
                      <div className="grid grid-cols-12 gap-4 items-center">
                        {/* Checkbox */}
                        <div className="col-span-1">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelection(party._id)}
                              className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header focus:ring-2 cursor-pointer"
                            />
                          </label>
                        </div>

                        {/* Name */}
                        <div className={`col-span-3 ${isUrdu ? 'text-right' : 'text-left'}`}>
                          <h3 className={`text-base font-bold text-gray-800 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {isUrdu ? (party.nameUrdu || party.name) : party.name}
                          </h3>
                        </div>

                        {/* Phone */}
                        <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                          <p className="text-sm text-gray-600">
                            {party.phone || '-'}
                          </p>
                        </div>

                        {/* Address */}
                        <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                          <p className={`text-sm text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                            {party.address ? (party.address.length > 30 ? party.address.substring(0, 30) + '...' : party.address) : '-'}
                          </p>
                        </div>

                        {/* Balance */}
                        <div className={`col-span-2 ${isUrdu ? 'text-right' : 'text-left'}`}>
                          <span className={`text-lg font-bold ${isReceivable ? 'text-emerald-600' : 'text-red-600'}`}>
                            Rs. {balanceAmount.toLocaleString()}
                          </span>
                        </div>

                        {/* Type Badge */}
                        <div className="col-span-1">
                          <div className={`px-2 py-1 rounded-full text-xs font-semibold inline-block ${isReceivable
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                              : 'bg-red-100 text-red-700 border border-red-200'
                            }`}>
                            {isReceivable ? (isUrdu ? 'قابل وصول' : 'Receivable') : (isUrdu ? 'قابل ادائیگی' : 'Payable')}
                          </div>
                        </div>

                        {/* Action Button */}
                        <div className="col-span-1">
                          <button
                            onClick={() => handleGenerateReceipt(party)}
                            disabled={generatingReceiptId === party._id}
                            className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {generatingReceiptId === party._id ? (
                              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                            )}
                            {generatingReceiptId === party._id
                              ? (isUrdu ? 'تیار ہو رہا ہے...' : 'Generating...')
                              : (isUrdu ? 'رسید' : 'Receipt')}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ReceiptManagement;

